import { useCallback, useEffect, useMemo, useState } from 'react';
import { request } from '../services/api';
import { startRazorpayRecharge } from '../modules/payments';
import { initialTransactions, walletPackages } from '../services/mockBackend';

const typeTitles = {
  register: 'Registration',
  recharge: 'Recharge',
  entry_fee: 'Entry Fee',
  refund: 'Refund',
  win_reward: 'Reward',
  match_loss: 'Result',
  match_status: 'Table',
  queue: 'Queue',
  admin_adjustment: 'Admin',
  signup_bonus: 'Signup Bonus',
  withdraw_request: 'Withdraw Request',
  withdraw_success: 'Withdraw Success',
  withdraw_reject: 'Withdraw Reject',
  withdraw_processing: 'Withdraw Processing',
  withdraw_failed: 'Withdraw Failed',
};

function amountToNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return Number(String(value || 0).replace(/[^\d-]/g, '') || 0);
}

function formatTime(value) {
  const date = new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return 'Just now';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAmount(value) {
  const amount = amountToNumber(value);
  return `${amount > 0 ? '+' : ''}${amount} Coins`;
}

function normalizeTransaction(item, index) {
  const type = String(item?.type || 'activity').toLowerCase();
  const amountValue = amountToNumber(item?.amountValue ?? item?.amount);
  const direction = item?.direction || (amountValue > 0 ? 'credit' : amountValue < 0 ? 'debit' : 'neutral');
  const createdAt = item?.createdAt || item?.time || new Date(Date.now() - (index * 1000)).toISOString();
  const reference = item?.reference || item?.tableRef || item?.matchId || 'General';
  return {
    id: item?.id || `txn_${createdAt}_${index}`,
    type,
    typeTitle: typeTitles[type] || String(type || 'Activity').replace(/_/g, ' '),
    label: item?.label || typeTitles[type] || 'Wallet Activity',
    amountValue,
    amount: item?.amount || formatAmount(amountValue),
    direction,
    createdAt,
    time: formatTime(createdAt),
    matchId: item?.matchId || null,
    tableRef: item?.tableRef || null,
    reference,
  };
}

function normalizeTransactions(transactions = []) {
  return transactions
    .map(normalizeTransaction)
    .filter((item) => item.amountValue !== 0);
}

export function useWallet(profile) {
  const [balance, setBalance] = useState(5250);
  const [transactions, setTransactions] = useState(() => normalizeTransactions(initialTransactions));
  const [packages, setPackages] = useState(walletPackages);
  const [paymentState, setPaymentState] = useState({ loading: false, message: 'Payment gateway idle.' });
  const [withdrawableCoins, setWithdrawableCoins] = useState(0);
  const [upiId, setUpiId] = useState('');
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [lastAction, setLastAction] = useState('Wallet waiting for backend sync.');

  const applyWalletUpdate = useCallback((payload, statusMessage = 'Wallet synced from backend.') => {
    if (!payload) return null;
    const hasBreakdown = ['depositCoins', 'winningCoins', 'referralCoins'].some((key) => payload[key] != null);
    const historyTotal = Array.isArray(payload.transactions)
      ? payload.transactions.reduce((sum, item) => sum + amountToNumber(item?.amountValue ?? item?.amount), 0)
      : Number.NaN;
    const computedBalance = hasBreakdown
      ? Number(payload.depositCoins || 0) + Number(payload.winningCoins || 0) + Number(payload.referralCoins || 0)
      : Number(payload.balance);
    const safeBalance = Number.isFinite(historyTotal) && Number.isFinite(computedBalance)
      ? Math.max(computedBalance, historyTotal)
      : computedBalance;
    if (Number.isFinite(safeBalance)) setBalance(safeBalance);
    if (Array.isArray(payload.transactions)) setTransactions(normalizeTransactions(payload.transactions));
    if (Array.isArray(payload.packages) && payload.packages.length) setPackages(payload.packages);
    if (payload.withdrawableCoins != null) setWithdrawableCoins(Number(payload.withdrawableCoins || 0));
    if (payload.upiId != null) setUpiId(String(payload.upiId || ''));
    setPaymentState({ loading: false, message: statusMessage });
    setLastAction(statusMessage);
    setLastSyncAt(new Date().toISOString());
    return payload;
  }, []);

  const loadWallet = useCallback(async (statusMessage = 'Wallet synced from backend.') => {
    if (!profile?.mobile) return null;
    try {
      const data = await request(`/api/wallet/${profile.mobile}`);
      return applyWalletUpdate(data, statusMessage);
    } catch (error) {
      const message = `Backend unavailable: ${error.message}`;
      setPaymentState({ loading: false, message });
      setLastAction(message);
      return null;
    }
  }, [applyWalletUpdate, profile?.mobile]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  const totals = useMemo(() => {
    const recharge = transactions
      .filter((item) => item.type === 'recharge')
      .reduce((sum, item) => sum + Math.max(0, item.amountValue), 0);
    const entrySpend = transactions
      .filter((item) => item.type === 'entry_fee')
      .reduce((sum, item) => sum + Math.abs(item.amountValue), 0);
    const rewardsEarned = transactions
      .filter((item) => item.type === 'win_reward')
      .reduce((sum, item) => sum + Math.max(0, item.amountValue), 0);

    return {
      totalRecharge: `${recharge} Coins`,
      entrySpend: `${entrySpend} Coins`,
      rewardsEarned: `${rewardsEarned} Coins`,
    };
  }, [transactions]);

  async function buyCoins(pkg) {
    try {
      const result = await startRazorpayRecharge({
        profile,
        pkg,
        onStatus: setPaymentState,
        onWalletUpdate: applyWalletUpdate,
      });
      if (result?.status === 'cancelled' || result?.status === 'failed') return result;
      return { status: 'success', creditedCoins: result?.payload?.creditedCoins || 0 };
    } catch (error) {
      setPaymentState({ loading: false, message: error.message });
      setLastAction(error.message);
      return { status: 'failed', error: error.message };
    }
  }

  const lastTransaction = transactions[0] || null;
  const lastSyncLabel = lastSyncAt ? `Synced ${formatTime(lastSyncAt)}` : 'Not synced yet';

  return {
    balance,
    totals,
    transactions,
    packages,
    paymentState,
    withdrawableCoins,
    upiId,
    buyCoins,
    refreshWallet: loadWallet,
    applyWalletUpdate,
    lastTransaction,
    lastAction,
    lastSyncLabel,
  };
}
